import { UntitledIcon } from '@faceup/icons'
import { ulCheckCircle } from '@faceup/icons/ulCheckCircle'
import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

export type OptionTileGroupProps = {
  children: ReactNode
  'aria-label'?: string
  'aria-describedby'?: string
  className?: string
}

export const OptionTileGroup = ({
  children,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  className,
}: OptionTileGroupProps) => (
  <fieldset
    aria-label={ariaLabel}
    aria-describedby={ariaDescribedBy}
    className={twMerge('m-0 flex flex-col gap-2 border-0 p-0', className)}
  >
    {children}
  </fieldset>
)

export type OptionTileProps = {
  type: 'radio' | 'checkbox'
  /** Radios of one question share it, so the browser groups them and arrow keys move the selection. */
  name: string
  value: string
  label: string
  checked: boolean
  onChange: () => void
  disabled?: boolean
  className?: string
}

export const OptionTile = ({
  type,
  name,
  value,
  label,
  checked,
  onChange,
  disabled,
  className,
}: OptionTileProps) => {
  const isCheckedRadio = type === 'radio' && checked

  return (
    <label
      className={twMerge(
        'group flex min-h-[54px] w-full cursor-pointer items-center justify-between gap-2 rounded-xl border border-colorBorder bg-colorBgContainer p-4',
        'has-[:checked]:border-colorPrimary has-[:checked]:bg-colorPrimary has-[:checked]:text-colorTextLightSolid',
        'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-colorPrimary has-[:focus-visible]:outline-offset-2',
        'has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-colorFillTertiary',
        className
      )}
    >
      <input
        type={type}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        onClick={isCheckedRadio ? onChange : undefined}
        onKeyUp={event => {
          // Chromium and WebKit dispatch no click for Space on a checked radio
          if (!isCheckedRadio || event.key !== ' ') {
            return
          }
          event.preventDefault()
          onChange()
        }}
        className='sr-only'
      />
      <span className='sensitive'>{label}</span>
      <UntitledIcon
        icon={ulCheckCircle}
        size={20}
        className='invisible shrink-0 group-has-[:checked]:visible'
      />
    </label>
  )
}
